import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Basket, Check, FloppyDisk, Clock, Warning,
  ClockCounterClockwise, X, CaretUp, StarFour, Barbell, CaretLeft, DownloadSimple,
} from "@phosphor-icons/react";
import { exportPorzionarioPdf } from "@/lib/exportPorzionario";
import { useConfirmDialog } from "@/components/ConfirmDialog";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const CATEGORIES = ["proteici", "amidacei", "frutta", "lipidici"];
const CAT_LABEL = {
  proteici: "Proteici",
  amidacei: "Amidacei",
  frutta: "Frutta",
  lipidici: "Lipidici",
};

// Trailing " 2" marker = second-choice food. Detect + strip for display.
const parseName = (raw = "") => {
  const m = raw.match(/^(.*?)\s+2\s*$/);
  return { display: m ? m[1] : raw, isSecondChoice: !!m };
};

const italianDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Portion assignment rules:
 *  - `is_second_choice` foods (" 2" marker) always take exactly 1 portion.
 *  - Remaining portions are distributed evenly among the selected regular foods.
 *  - `needsMore` / `excessSeconds` drive inline warnings on the UI.
 */
function computeCategoryAssignment(portionsNeeded, selectedFoods) {
  if (!portionsNeeded || portionsNeeded <= 0) {
    return { items: [], covered: 0, complete: true, needsMore: false, excessSeconds: false };
  }
  const seconds = selectedFoods.filter((f) => f.is_second_choice);
  const regulars = selectedFoods.filter((f) => !f.is_second_choice);
  const excessSeconds = seconds.length > portionsNeeded;
  const validSeconds = seconds.slice(0, portionsNeeded);
  const remaining = Math.max(0, portionsNeeded - validSeconds.length);
  const items = validSeconds.map((f) => ({
    food_id: f.food_id,
    name: f.name,
    base_grams: f.grams,
    portions: 1,
    computed_grams: Math.round(f.grams),
    is_second_choice: true,
  }));
  if (remaining > 0 && regulars.length > 0) {
    const per = remaining / regulars.length;
    for (const f of regulars) {
      items.push({
        food_id: f.food_id,
        name: f.name,
        base_grams: f.grams,
        portions: per,
        computed_grams: Math.round(f.grams * per),
        is_second_choice: false,
      });
    }
  }
  const covered = validSeconds.length + (regulars.length > 0 ? remaining : 0);
  const complete = Math.abs(covered - portionsNeeded) < 0.001 && !excessSeconds;
  const needsMore = covered < portionsNeeded;
  return { items, covered, complete, needsMore, excessSeconds };
}

function FoodRow({ food, selected, disabled, onToggle }) {
  const { display, isSecondChoice } = parseName(food.name);
  return (
    <button
      type="button"
      data-testid={`food-chip-${food.food_id || food.name}`}
      disabled={disabled}
      onClick={onToggle}
      className="w-full flex items-start justify-between gap-3 px-4 py-2.5 text-left transition-colors"
      style={{
        background: selected ? "rgba(166,93,71,0.10)" : "transparent",
        color: disabled ? "var(--text-secondary)" : "var(--text-primary)",
        opacity: disabled ? 0.45 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      <span className="min-w-0 flex-1 text-[15px] leading-snug break-words">
        {display}
        {isSecondChoice && (
          <sup className="font-bold ml-0.5 text-[0.7em] leading-none align-super" data-testid="second-choice-marker">2</sup>
        )}
      </span>
      {selected && (
        <span
          className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full inline-flex items-center justify-center"
          style={{ background: "var(--accent)", color: "#fff" }}
        >
          <Check size={12} weight="bold" />
        </span>
      )}
    </button>
  );
}

/** One AccordionItem per category, closed by default. */
function CategorySection({ mealId, category, portions, foods, selectedIds, onToggle }) {
  const isActive = portions > 0;
  const selectedFoods = foods.filter((f) => selectedIds.includes(f.food_id));
  const assignment = useMemo(
    () => computeCategoryAssignment(portions, selectedFoods),
    [portions, selectedFoods],
  );
  return (
    <AccordionItem
      value={`${mealId}::${category}`}
      data-testid={`cat-section-${mealId}-${category}`}
      className="border-b border-[color:var(--border)] last:border-b-0"
    >
      <AccordionTrigger
        data-testid={`cat-trigger-${mealId}-${category}`}
        className="px-4 py-3 hover:no-underline hover:bg-[color:var(--bg-secondary)] transition-colors"
        style={{ opacity: isActive ? 1 : 0.6 }}
      >
        <div className="flex items-center justify-between gap-3 w-full pr-2 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: isActive ? "var(--accent)" : "var(--text-secondary)" }}
            />
            <span
              className="font-serif text-base sm:text-lg truncate"
              style={{ color: isActive ? "var(--text-primary)" : "var(--text-secondary)" }}
            >
              {CAT_LABEL[category]}
            </span>
            {isActive && selectedFoods.length > 0 && assignment.complete && (
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full flex-shrink-0"
                style={{ background: "#4A7C59", color: "#fff" }}
                aria-label="Categoria completa"
              >
                <Check size={9} weight="bold" />
              </span>
            )}
          </div>
          {isActive ? (
            <span
              data-testid={`cat-portions-${mealId}-${category}`}
              className="text-xs font-semibold px-2.5 py-1 rounded-full tabular-nums flex-shrink-0"
              style={{ background: "var(--accent)", color: "#fff" }}
            >
              {portions} {portions === 1 ? "porzione" : "porzioni"}
            </span>
          ) : (
            <span className="text-[11px] italic text-[color:var(--text-secondary)] flex-shrink-0">
              Non prevista
            </span>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent className="pb-0">
        <ul className="divide-y divide-[color:var(--border)]">
          {foods.map((f) => (
            <li key={f.food_id || f.name}>
              <FoodRow
                food={f}
                disabled={!isActive}
                selected={selectedIds.includes(f.food_id)}
                onToggle={() => isActive && onToggle(f.food_id)}
              />
            </li>
          ))}
        </ul>
      </AccordionContent>
    </AccordionItem>
  );
}

/** A single meal block: heading + 4-category accordion (all closed by default). */
function MealBlock({ meal, foodDb, mealSelections, onToggle }) {
  return (
    <section data-testid={`meal-block-${meal.id}`} className="scroll-mt-4">
      <header
        className="flex items-center gap-3 mb-2 mt-2 sm:mt-4"
        data-testid={`meal-heading-${meal.id}`}
      >
        <div className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs tabular-nums" style={{ background: "var(--text-primary)", color: "#fff" }}>
          <Clock size={11} weight="fill" />
          {meal.time || "—"}
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl truncate">
          {meal.name}
        </h2>
      </header>
      <div className="rounded-2xl border-2 border-[color:var(--border)] overflow-hidden bg-[color:var(--bg-primary)]">
        <Accordion type="multiple" className="w-full">
          {CATEGORIES.map((cat) => {
            const need = Number(meal.portions?.[cat] || 0);
            const catFoods = (foodDb?.foods?.[cat] || []).map((f) => ({
              ...f,
              is_second_choice: /\s+2\s*$/.test(f.name),
            }));
            return (
              <CategorySection
                key={cat}
                mealId={meal.id}
                category={cat}
                portions={need}
                foods={catFoods}
                selectedIds={(mealSelections || {})[cat] || []}
                onToggle={(fid) => onToggle(cat, fid)}
              />
            );
          })}
        </Accordion>
      </div>
    </section>
  );
}

/**
 * Workout block for the main meals stack — informational only, NO
 * nutritional interface (no categories, no accordion, no portions). Just
 * the chronological time + a free-text description echoing what the admin
 * planned in the plan editor.
 */
function WorkoutBlock({ workout }) {
  return (
    <section
      data-testid={`workout-block-${workout.id}`}
      className="scroll-mt-4"
    >
      <header className="flex items-center gap-3 mb-2 mt-2 sm:mt-4">
        <div
          className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs tabular-nums"
          style={{ background: "#2A4C63", color: "#fff" }}
        >
          <Clock size={11} weight="fill" />
          {workout.time || "—"}
        </div>
        <h2 className="font-serif text-2xl sm:text-3xl truncate" style={{ color: "#2A4C63" }}>
          Allenamento
        </h2>
      </header>
      <div
        className="rounded-2xl border-2 p-4 sm:p-5 flex items-start gap-3"
        style={{ borderColor: "#B8D4E3", background: "#EAF3F8" }}
      >
        <Barbell size={22} weight="duotone" style={{ color: "#3F6E8A", flexShrink: 0, marginTop: 2 }} />
        <div className="min-w-0 flex-1">
          {workout.text ? (
            <div className="text-sm sm:text-base whitespace-pre-wrap leading-snug" style={{ color: "#2A4C63" }}>
              {workout.text}
            </div>
          ) : (
            <div className="text-xs italic" style={{ color: "#3F6E8A" }}>
              Nessuna descrizione fornita dal coach.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Fixed bottom summary bar — aggregates ALL meals of the active plan.
 * Save buttons live INSIDE the expanded sheet. Portal to `document.body`
 * to escape the transformed `<main>` ancestor (`.fade-up` animation).
 */
function BottomSummaryBar({
  plan, foodDb, allSelections,
  forceOpen, onForceOpenHandled,
  isEditing, editingLabel,
  onSave, saving,
}) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setExpanded(true);
      onForceOpenHandled?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen]);

  // Close automatically when active plan changes.
  useEffect(() => { setExpanded(false); }, [plan?.plan_id]);

  const summary = useMemo(() => {
    const blocks = [];
    let totalItems = 0;
    let portionsNeeded = 0;
    let portionsCovered = 0;
    if (!plan || !foodDb) return { blocks, totalItems, portionsNeeded, portionsCovered };
    const meals = [...(plan.meals || [])];
    for (const meal of meals) {
      const catBlocks = [];
      let mealItems = 0;
      for (const cat of CATEGORIES) {
        const need = Number(meal.portions?.[cat] || 0);
        if (need <= 0) continue;
        portionsNeeded += need;
        const ids = ((allSelections || {})[meal.id] || {})[cat] || [];
        const catFoods = (foodDb.foods?.[cat] || [])
          .filter((f) => ids.includes(f.food_id))
          .map((f) => ({ ...f, is_second_choice: /\s+2\s*$/.test(f.name) }));
        const a = computeCategoryAssignment(need, catFoods);
        totalItems += catFoods.length;
        mealItems += catFoods.length;
        portionsCovered += a.covered;
        if (a.items.length > 0) {
          catBlocks.push({ category: cat, need, ...a });
        }
      }
      if (mealItems > 0) {
        blocks.push({ type: "meal", time: meal.time || "", meal, itemsCount: mealItems, cats: catBlocks });
      }
    }
    // Workouts always shown in the summary for temporal context (they don't
    // affect macros nor the coverage bar — purely informative).
    for (const w of (plan.workouts || [])) {
      blocks.push({ type: "workout", time: w.time || "", workout: w });
    }
    blocks.sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    return { blocks, totalItems, portionsNeeded, portionsCovered };
  }, [plan, foodDb, allSelections]);

  if (!plan || summary.totalItems === 0) return null;

  const coveragePct = summary.portionsNeeded > 0
    ? Math.min(100, Math.round((summary.portionsCovered / summary.portionsNeeded) * 100))
    : 0;

  return createPortal(
    <>
      {expanded && (
        <div
          className="fixed inset-0 bg-black/40 z-[60]"
          onClick={() => setExpanded(false)}
          aria-hidden="true"
        />
      )}
      <div
        data-testid="bottom-summary-bar"
        className="fixed bottom-0 left-0 right-0 z-[70] pointer-events-none"
      >
        <div className="mx-auto max-w-4xl px-2 sm:px-4 pointer-events-auto">
          <div
            className="rounded-t-2xl border-2 border-b-0 bg-[color:var(--bg-primary)] shadow-[0_-6px_20px_rgba(26,26,24,0.10)]"
            style={{ borderColor: "var(--border)" }}
          >
            <button
              type="button"
              data-testid="bottom-summary-toggle"
              onClick={() => setExpanded((v) => !v)}
              className="w-full flex items-center gap-3 px-4 py-3 text-left"
            >
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">La tua giornata</span>
                  {isEditing && (
                    <span
                      data-testid="editing-badge"
                      className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                      style={{ background: "var(--accent-2)", color: "#fff" }}
                    >
                      In modifica
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-sm tabular-nums">
                  <strong>{summary.totalItems}</strong> aliment{summary.totalItems === 1 ? "o" : "i"} · {summary.blocks.filter((b) => b.type === "meal").length} pasti
                  {summary.portionsNeeded > 0 && (
                    <span className="text-[color:var(--text-secondary)]"> · {coveragePct}% coperto</span>
                  )}
                </div>
                <div className="mt-1.5 h-1 rounded-full overflow-hidden" style={{ background: "var(--bg-secondary)" }}>
                  <div
                    className="h-full transition-all"
                    style={{
                      width: `${coveragePct}%`,
                      background: coveragePct >= 100 ? "#4A7C59" : "var(--accent)",
                    }}
                  />
                </div>
              </div>
              <CaretUp
                size={16}
                weight="bold"
                className="transition-transform flex-shrink-0"
                style={{ transform: expanded ? "rotate(180deg)" : "rotate(0)" }}
              />
            </button>

            {expanded && (
              <div
                data-testid="bottom-summary-detail"
                className="border-t border-[color:var(--border)] max-h-[65vh] overflow-y-auto"
              >
                <div className="px-4 py-3 space-y-5">
                  {isEditing && editingLabel && (
                    <div className="rounded-lg border border-[color:var(--accent-2)] bg-[rgba(74,124,89,0.06)] px-3 py-2 text-xs">
                      <span className="text-[color:var(--text-secondary)]">Stai modificando: </span>
                      <strong>{editingLabel}</strong>
                    </div>
                  )}
                  {summary.blocks.length === 0 ? (
                    <div className="text-sm text-[color:var(--text-secondary)] italic py-2">
                      Nessuna scelta ancora inserita per questa giornata.
                    </div>
                  ) : summary.blocks.map((b) => {
                    if (b.type === "workout") {
                      const w = b.workout;
                      return (
                        <div
                          key={`w-${w.id}`}
                          data-testid={`summary-workout-${w.id}`}
                          className="rounded-lg border px-3 py-2"
                          style={{ borderColor: "#B8D4E3", background: "rgba(232,243,248,0.55)" }}
                        >
                          <div className="flex items-center gap-2">
                            <div className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] tabular-nums" style={{ background: "#2A4C63", color: "#fff" }}>
                              <Clock size={9} weight="fill" />
                              {w.time || "—"}
                            </div>
                            <Barbell size={14} weight="duotone" style={{ color: "#3F6E8A" }} />
                            <div className="font-serif text-base sm:text-lg" style={{ color: "#2A4C63" }}>Allenamento</div>
                          </div>
                          {w.text && (
                            <div className="mt-1 pl-1 text-xs sm:text-sm whitespace-pre-wrap" style={{ color: "#2A4C63" }}>
                              {w.text}
                            </div>
                          )}
                        </div>
                      );
                    }
                    const { meal, cats } = b;
                    return (
                    <div key={meal.id} data-testid={`summary-meal-${meal.id}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] tabular-nums" style={{ background: "var(--text-primary)", color: "#fff" }}>
                          <Clock size={9} weight="fill" />
                          {meal.time || "—"}
                        </div>
                        <div className="font-serif text-base sm:text-lg truncate">{meal.name}</div>
                      </div>
                      <div className="pl-1 space-y-3">
                        {cats.map(({ category, need, items, complete, excessSeconds }) => (
                          <div key={category}>
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "var(--accent)" }} />
                                <div className="text-sm font-medium">{CAT_LABEL[category]}</div>
                                {complete && (
                                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full" style={{ background: "#4A7C59", color: "#fff" }}>
                                    <Check size={9} weight="bold" />
                                  </span>
                                )}
                              </div>
                              <span className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] tabular-nums">
                                {need} porz.
                              </span>
                            </div>
                            <ul className="space-y-1 text-sm pl-3.5">
                              {items.map((it, i) => {
                                const { display, isSecondChoice } = parseName(it.name);
                                return (
                                  <li key={`${it.food_id || it.name}-${i}`} className="flex items-start justify-between gap-3">
                                    <span className="min-w-0 flex-1 break-words leading-snug">
                                      {display}
                                      {isSecondChoice && <sup className="font-bold ml-0.5 text-[0.7em] leading-none align-super">2</sup>}
                                    </span>
                                    <span
                                      data-testid={`grams-${meal.id}-${category}-${it.food_id || i}`}
                                      className="tabular-nums text-sm font-semibold flex-shrink-0"
                                      style={{ color: "var(--accent)" }}
                                    >
                                      {it.computed_grams}g
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                            {!complete && (
                              <div className="mt-1 ml-3.5 inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full" style={{ background: "rgba(185,74,74,0.10)", color: "#B94A4A" }}>
                                <Warning size={11} weight="fill" />
                                {excessSeconds
                                  ? "Troppi alimenti di seconda scelta"
                                  : `Aggiungi altri alimenti per coprire ${need} porzioni`}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                    );
                  })}
                </div>

                {/* Save action lives INSIDE the sheet, sticky at bottom */}
                <div
                  className="sticky bottom-0 border-t border-[color:var(--border)] bg-[color:var(--bg-primary)] px-4 py-3 flex justify-end gap-2"
                >
                  <Button
                    data-testid="save-composition-btn"
                    onClick={onSave}
                    disabled={saving}
                    className="rounded-full h-10 bg-[color:var(--accent)] text-white hover:bg-[color:var(--accent-hover)] hover:text-white px-5"
                  >
                    <FloppyDisk size={14} weight="bold" className="mr-1.5" />
                    {isEditing ? "Salva modifiche" : "Salva giornata"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

export default function ClientPorzionario() {
  const { user } = useAuth();
  const { clientId: adminClientId } = useParams();  // present only on admin route
  const [searchParams] = useSearchParams();
  const adminMode = !!adminClientId;
  const clientId = adminClientId || user?.user_id;
  const [adminClient, setAdminClient] = useState(null);  // client profile in admin mode
  const [plans, setPlans] = useState([]);
  const [activePlanId, setActivePlanId] = useState(null);
  const [foodDb, setFoodDb] = useState(null);
  const [history, setHistory] = useState([]);
  const [selections, setSelections] = useState({});   // keyed by meal.id
  const [editingCompositionId, setEditingCompositionId] = useState(null);
  const [forceOpenSummary, setForceOpenSummary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const { confirm, dialog } = useConfirmDialog();
  const [studio, setStudio] = useState(null);

  useEffect(() => {
    api.get("/studio-settings").then(({ data }) => setStudio(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!clientId) return;
    let alive = true;
    (async () => {
      try {
        // In admin mode we first fetch the client profile to get the correct
        // gender for the food-db request; in client mode we already have it.
        let effectiveGender = user?.gender || "M";
        if (adminMode) {
          const { data: c } = await api.get(`/clients/${clientId}`);
          if (!alive) return;
          setAdminClient(c);
          effectiveGender = c?.gender || "M";
        }
        const [plansRes, foodDbRes, histRes] = await Promise.all([
          api.get(`/clients/${clientId}/plans`),
          api.get(`/nutrition/food-db?gender=${effectiveGender}`),
          api.get(`/clients/${clientId}/compositions`),
        ]);
        if (!alive) return;
        const allPlans = plansRes.data || [];
        setPlans(allPlans);
        setFoodDb(foodDbRes.data);
        const histList = histRes.data || [];
        setHistory(histList);
        // If admin arrived with ?comp=<id>, auto-recall that composition so
        // the editor opens directly on the day the admin wanted to review.
        const compParam = searchParams.get("comp");
        const target = compParam ? histList.find((h) => h.composition_id === compParam) : null;
        if (target) {
          const next = {};
          for (const m of target.meals || []) {
            const catMap = {};
            for (const cat of CATEGORIES) {
              catMap[cat] = ((m.selections || {})[cat] || [])
                .map((it) => it.food_id)
                .filter(Boolean);
            }
            next[m.meal_id] = catMap;
          }
          setSelections(next);
          setEditingCompositionId(target.composition_id);
          setActivePlanId(target.plan_id || (allPlans.find((p) => p.active) || allPlans[0] || {}).plan_id || null);
          setForceOpenSummary(true);
        } else {
          const active = allPlans.find((p) => p.active) || allPlans[0] || null;
          setActivePlanId(active?.plan_id || null);
        }
      } catch (e) {
        toast.error("Errore nel caricamento");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [clientId, user?.gender, adminMode]);  // eslint-disable-line react-hooks/exhaustive-deps

  const activePlan = useMemo(
    () => plans.find((p) => p.plan_id === activePlanId) || null,
    [plans, activePlanId],
  );

  const sortedBlocks = useMemo(() => {
    const meals = ((activePlan?.meals) || []).map((m) => ({ type: "meal", time: m.time || "", meal: m }));
    const workouts = ((activePlan?.workouts) || []).map((w) => ({ type: "workout", time: w.time || "", workout: w }));
    return [...meals, ...workouts].sort((a, b) => (a.time || "").localeCompare(b.time || ""));
  }, [activePlan]);

  const sortedMeals = useMemo(
    () => sortedBlocks.filter((b) => b.type === "meal").map((b) => b.meal),
    [sortedBlocks],
  );

  const toggleFood = (mealId, category, foodId) => {
    setSelections((prev) => {
      const meal = prev[mealId] || {};
      const list = meal[category] || [];
      const next = list.includes(foodId) ? list.filter((x) => x !== foodId) : [...list, foodId];
      return { ...prev, [mealId]: { ...meal, [category]: next } };
    });
  };

  const switchPlan = async (planId) => {
    if (planId === activePlanId) return;
    // Warn if user is editing / has selections in the current plan.
    const hasWork = editingCompositionId || Object.keys(selections).length > 0;
    if (hasWork) {
      const ok = await confirm({
        title: "Cambiare piano?",
        description: "Le selezioni non salvate della giornata verranno perse.",
        confirmLabel: "Cambia piano",
      });
      if (!ok) return;
    }
    setSelections({});
    setEditingCompositionId(null);
    setActivePlanId(planId);
  };

  const buildMealsPayload = () => sortedMeals.map((m) => {
    const perCat = {};
    for (const cat of CATEGORIES) {
      const need = Number(m.portions?.[cat] || 0);
      const ids = (selections[m.id] || {})[cat] || [];
      const foods = (foodDb?.foods?.[cat] || [])
        .filter((f) => ids.includes(f.food_id))
        .map((f) => ({ ...f, is_second_choice: /\s+2\s*$/.test(f.name) }));
      const a = computeCategoryAssignment(need, foods);
      perCat[cat] = a.items;
    }
    return {
      meal_id: m.id,
      meal_name: m.name,
      meal_time: m.time || "",
      selections: perCat,
    };
  });

  const editingLabel = useMemo(
    () => history.find((h) => h.composition_id === editingCompositionId)?.label || "",
    [history, editingCompositionId],
  );

  const save = async () => {
    if (!activePlan) return;
    setSaving(true);
    try {
      const meals = buildMealsPayload();
      if (editingCompositionId) {
        const { data } = await api.patch(`/compositions/${editingCompositionId}`, { meals });
        setHistory((h) => h.map((x) => x.composition_id === editingCompositionId ? data : x));
        toast.success(`Modifiche salvate: ${data.label}`);
      } else {
        const { data } = await api.post(`/clients/${clientId}/compositions`, {
          plan_id: activePlan.plan_id,
          plan_name: activePlan.name,
          meals,
        });
        setHistory((h) => [data, ...h]);
        setEditingCompositionId(data.composition_id);
        toast.success(`Salvato: ${data.label}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  const recallComposition = (comp) => {
    // Populate selections from a saved composition and open the summary sheet
    // so the client immediately sees the past choices without scrolling.
    const next = {};
    for (const m of comp.meals || []) {
      const catMap = {};
      for (const cat of CATEGORIES) {
        catMap[cat] = ((m.selections || {})[cat] || [])
          .map((it) => it.food_id)
          .filter(Boolean);
      }
      next[m.meal_id] = catMap;
    }
    setSelections(next);
    setEditingCompositionId(comp.composition_id);
    if (comp.plan_id && comp.plan_id !== activePlanId && plans.some((p) => p.plan_id === comp.plan_id)) {
      setActivePlanId(comp.plan_id);
    }
    setHistoryOpen(false);
    setForceOpenSummary(true);
    toast.success(`Aperto: ${comp.label}`);
  };

  const removeComposition = async (comp) => {
    const ok = await confirm({
      title: "Eliminare il salvataggio?",
      description: `«${comp.label}» sarà rimosso dallo storico.`,
      confirmLabel: "Elimina",
    });
    if (!ok) return;
    try {
      await api.delete(`/compositions/${comp.composition_id}`);
      setHistory((h) => h.filter((x) => x.composition_id !== comp.composition_id));
      if (editingCompositionId === comp.composition_id) {
        setEditingCompositionId(null);
      }
      toast.success("Salvataggio eliminato");
    } catch (e) { toast.error("Errore eliminazione"); }
  };

  const startNewDay = async () => {
    if (Object.keys(selections).length > 0 || editingCompositionId) {
      const ok = await confirm({
        title: "Iniziare una nuova giornata?",
        description: "Le selezioni non salvate saranno perse.",
        confirmLabel: "Nuova giornata",
      });
      if (!ok) return;
    }
    setSelections({});
    setEditingCompositionId(null);
  };

  if (loading) {
    return <div className="label-tiny">Caricamento…</div>;
  }
  if (plans.length === 0) {
    return (
      <div data-testid="empty-state-wrap">
        {adminMode && (
          <Link
            to={`/admin/clients/${clientId}?sec=days`}
            className="inline-flex items-center gap-1 label-tiny hover:text-[color:var(--accent)]"
          >
            <CaretLeft size={14} weight="bold" /> Scheda cliente
          </Link>
        )}
        <div className="mt-4 rounded-2xl border-2 border-[color:var(--border)] p-8 text-center" data-testid="empty-state">
          <div className="font-serif text-2xl">Nessun piano attivo</div>
          <div className="text-sm text-[color:var(--text-secondary)] mt-2">
            {adminMode
              ? "Il cliente non ha ancora un piano assegnato. Assegna un piano per abilitare il porzionario."
              : "Il tuo coach non ha ancora attivato un piano. Il porzionario si attiverà appena sarà disponibile."}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="client-porzionario">
      {adminMode && (
        <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to={`/admin/clients/${clientId}?sec=days`}
            className="inline-flex items-center gap-1 label-tiny hover:text-[color:var(--accent)]"
            data-testid="admin-back-to-client"
          >
            <CaretLeft size={14} weight="bold" /> Scheda cliente
          </Link>
          <div
            data-testid="admin-mode-badge"
            className="text-[10px] uppercase tracking-widest px-3 py-1 rounded-full"
            style={{ background: "var(--accent)", color: "#fff" }}
          >
            Modalità admin · {adminClient?.name || "Cliente"}
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="label-tiny">Porzionario dinamico</div>
          <h1 className="font-serif text-4xl sm:text-5xl leading-tight mt-1">
            {activePlan?.name || "Componi la giornata"}
          </h1>
          <div className="text-sm text-[color:var(--text-secondary)] mt-2 max-w-lg">
            Scegli gli alimenti per ogni pasto del piano. Il riepilogo con le grammature calcolate si apre dal fondo dello schermo.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            data-testid="client-export-porzionario-btn"
            variant="outline"
            disabled={!foodDb}
            onClick={() => exportPorzionarioPdf({ foodDb, studio, gender: adminClient?.gender || user?.gender || "M" })}
            className="rounded-full h-9 border-2"
          >
            <DownloadSimple size={14} weight="bold" className="mr-1.5" />
            Porzionario PDF
          </Button>
          <Button
            data-testid="history-btn"
            variant="outline"
            onClick={() => setHistoryOpen((v) => !v)}
            className="rounded-full h-9 border-2"
          >
            <ClockCounterClockwise size={14} weight="duotone" className="mr-1.5" />
            I miei salvataggi ({history.length})
          </Button>
        </div>
      </div>

      {/* Plan picker — all plans of the client, not just the active one */}
      {plans.length > 1 && (
        <div className="mt-6 flex flex-wrap gap-2" data-testid="plan-picker">
          {plans.map((p) => {
            const active = p.plan_id === activePlanId;
            return (
              <button
                key={p.plan_id}
                type="button"
                data-testid={`plan-chip-${p.plan_id}`}
                onClick={() => switchPlan(p.plan_id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 text-xs sm:text-sm transition-all"
                style={{
                  borderColor: active ? "var(--text-primary)" : "var(--border)",
                  background: active ? "var(--text-primary)" : "var(--bg-primary)",
                  color: active ? "#fff" : "var(--text-primary)",
                }}
              >
                {p.active && <StarFour size={11} weight="fill" style={{ color: active ? "#fff" : "var(--accent-2)" }} />}
                <span className="truncate max-w-[180px] sm:max-w-[240px]">{p.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* History panel */}
      {historyOpen && (
        <div
          data-testid="history-panel"
          className="mt-4 rounded-2xl border border-[color:var(--border)] p-4 bg-[color:var(--bg-primary)]"
        >
          <div className="label-tiny mb-2">Salvataggi precedenti</div>
          {history.length === 0 ? (
            <div className="text-sm text-[color:var(--text-secondary)] italic">Nessun salvataggio ancora. Componi i pasti e tocca "Salva giornata".</div>
          ) : (
            <ul className="space-y-1.5 max-h-80 overflow-auto pr-1">
              {history.map((c) => (
                <li
                  key={c.composition_id}
                  data-testid={`history-${c.composition_id}`}
                  className="flex items-center gap-2 rounded-lg border border-[color:var(--border)] px-3 py-2 text-sm hover:border-[color:var(--text-primary)] transition-colors"
                >
                  <button
                    type="button"
                    data-testid={`history-recall-${c.composition_id}`}
                    onClick={() => recallComposition(c)}
                    className="flex-1 min-w-0 text-left"
                    title="Apri e riutilizza"
                  >
                    <div
                      className="font-medium truncate"
                      data-testid={`history-plan-name-${c.composition_id}`}
                    >
                      {c.plan_name || c.label}
                    </div>
                    <div
                      className="mt-0.5 text-[11px] tabular-nums text-[color:var(--text-secondary)] inline-flex items-center gap-1"
                      data-testid={`history-date-${c.composition_id}`}
                    >
                      <Clock size={10} weight="fill" />
                      {italianDate(c.date)}
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeComposition(c)}
                    className="w-7 h-7 inline-flex items-center justify-center rounded-full border border-[color:var(--border)] text-red-700 hover:bg-red-50 hover:border-red-300"
                    title="Elimina salvataggio"
                  >
                    <X size={12} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Editing banner */}
      {editingCompositionId && (
        <div
          data-testid="editing-banner"
          className="mt-4 rounded-xl px-4 py-2.5 flex items-center gap-3 flex-wrap"
          style={{ background: "rgba(74,124,89,0.10)", borderLeft: "3px solid var(--accent-2)" }}
        >
          <span className="text-xs">
            <span className="text-[color:var(--text-secondary)] uppercase tracking-widest text-[10px]">In modifica: </span>
            <strong>{editingLabel}</strong>
          </span>
          <button
            type="button"
            data-testid="new-day-btn"
            onClick={startNewDay}
            className="ml-auto text-xs underline text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"
          >
            Nuova giornata
          </button>
        </div>
      )}

      {/* All meals + workouts rendered in sequence — sorted by time.
          Workouts have NO nutritional interface (no accordion, no
          categories) — only the chronological position + description. */}
      <div className="mt-4 space-y-4" data-testid="meals-stack">
        {sortedBlocks.length === 0 ? (
          <div className="text-sm text-[color:var(--text-secondary)] italic">Questo piano non contiene ancora pasti.</div>
        ) : sortedBlocks.map((b) => {
          if (b.type === "workout") {
            return <WorkoutBlock key={`w-${b.workout.id}`} workout={b.workout} />;
          }
          const meal = b.meal;
          return (
            <MealBlock
              key={meal.id}
              meal={meal}
              foodDb={foodDb}
              mealSelections={selections[meal.id]}
              onToggle={(cat, fid) => toggleFood(meal.id, cat, fid)}
            />
          );
        })}
      </div>

      <div className="mt-6 flex items-center gap-2 flex-wrap text-xs text-[color:var(--text-secondary)]">
        <Basket size={13} weight="duotone" />
        <span>
          Il riepilogo e il pulsante di salvataggio si aprono dalla barra in basso.
        </span>
      </div>

      {/* Legend — placed BEFORE the bottom spacer so it sits well above the
          fixed BottomSummaryBar (the spacer below acts as clearance for the
          bar). */}
      <div
        data-testid="food-legend"
        className="mt-6 rounded-xl border border-[color:var(--border)] p-4 text-[11px] leading-relaxed"
        style={{ background: "var(--bg-secondary)", color: "var(--text-secondary)" }}
      >
        <div className="label-tiny mb-2" style={{ color: "var(--text-primary)" }}>Legenda</div>
        <div className="grid gap-1.5 sm:grid-cols-2">
          <div className="flex gap-2">
            <span className="font-bold text-[color:var(--text-primary)] tabular-nums flex-shrink-0 w-6">*</span>
            <span>cereali senza glutine</span>
          </div>
          <div className="flex gap-2">
            <span className="font-bold text-[color:var(--text-primary)] tabular-nums flex-shrink-0 w-6">**</span>
            <span>alimenti limitati al post-workout (ove previsto)</span>
          </div>
          <div className="flex gap-2">
            <span className="font-bold text-[color:var(--text-primary)] tabular-nums flex-shrink-0 w-6">#</span>
            <span>frutti che possono essere consumati anche alla fine di un pasto completo</span>
          </div>
          <div className="flex gap-2">
            <span className="font-bold text-[color:var(--text-primary)] tabular-nums flex-shrink-0 w-6">2</span>
            <span>alimenti di seconda scelta</span>
          </div>
        </div>
      </div>

      {/* Bottom clearance so page content (incl. the legend above) doesn't
          hide behind the fixed BottomSummaryBar. Sized to comfortably fit
          the collapsed bar + safe area. */}
      <div className="h-32" aria-hidden="true" />

      <BottomSummaryBar
        plan={activePlan}
        foodDb={foodDb}
        allSelections={selections}
        forceOpen={forceOpenSummary}
        onForceOpenHandled={() => setForceOpenSummary(false)}
        isEditing={!!editingCompositionId}
        editingLabel={editingLabel}
        onSave={save}
        saving={saving}
      />
      {dialog}
    </div>
  );
}
